import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  X, 
  Download, 
  FileText, 
  Play, 
  RefreshCw,
  Copy,
  Check,
  AlertCircle,
  Info,
  FileType,
  Calendar,
  User,
  Tag,
  Settings
} from 'lucide-react';
import TemplateService from '../../services/templateService';
import '../../styles/asset-lifecycle-modal.css';

const TemplatePreview = ({ 
  isOpen, 
  onClose, 
  template, 
  onGenerate 
}) => {
  const [previewContent, setPreviewContent] = useState('');
  const [sampleData, setSampleData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');

  useEffect(() => {
    if (isOpen && template) {
      loadPreview();
    }
  }, [isOpen, template]);

  const loadPreview = async () => {
    if (!template) return;
    
    setIsLoading(true);
    try {
      const sampleData = TemplateService.getSampleContractData();
      setSampleData(sampleData);
      
      const response = await TemplateService.previewTemplate(template.id, sampleData);
      if (response.success) {
        setPreviewContent(response.data.previewContent);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewContent('Error loading preview content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateDocument = async () => {
    if (!template) return;
    
    setIsGenerating(true);
    try {
      await onGenerate(template);
    } catch (error) {
      console.error('Error generating document:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const formatPlaceholderValue = (placeholder, value) => {
    if (!value) return `[${placeholder.name}]`;
    
    switch (placeholder.dataType) {
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'currency':
        return `$${value}`;
      case 'number':
        return value.toString();
      default:
        return value;
    }
  };

  if (!isOpen || !template) return null;

  const tabs = [
    { id: 'preview', label: 'Preview', icon: Eye },
    { id: 'placeholders', label: 'Placeholders', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      animation: 'fadeIn 0.2s ease-in-out'
    }}>
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '100%',
        maxWidth: '800px',
        height: '100vh',
        backgroundColor: 'white',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideInFromRight 0.3s ease-out',
        overflow: 'hidden'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        {/* Header with blue theme */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          backgroundColor: '#4678be',
          color: 'white',
          flexShrink: 0
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'white',
              margin: '0 0 4px 0',
              fontFamily: 'var(--font-family)'
            }}>
              {template.name}
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0,
              fontFamily: 'var(--font-family)'
            }}>
              {template.description || 'No description provided'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={handleGenerateDocument}
              disabled={isGenerating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: isGenerating ? '#9ca3af' : '#4678be',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '0',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family)'
              }}
            >
              {isGenerating ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Generate Document
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: 'white'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--secondary-color)',
          flexShrink: 0
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? '#4678be' : 'var(--text-secondary)',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid #4678be' : '2px solid transparent',
                fontFamily: 'var(--font-family)',
                borderRadius: '0'
              }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '24px'
        }}>
          {activeTab === 'preview' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Template Preview
                </h3>
                <button
                  onClick={loadPreview}
                  disabled={isLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: 'var(--accent-color)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>

              {isLoading ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px',
                  color: '#6b7280'
                }}>
                  <RefreshCw size={20} className="animate-spin" />
                  <span style={{ marginLeft: '8px', fontFamily: 'var(--font-family)', color: 'var(--text-secondary)' }}>Loading preview...</span>
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'var(--secondary-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0',
                  padding: '20px',
                  fontFamily: 'var(--font-family)',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  minHeight: '300px',
                  color: 'var(--text-primary)'
                }}>
                  {previewContent || 'No preview content available'}
                </div>
              )}
            </div>
          )}

          {activeTab === 'placeholders' && (
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: '0 0 20px 0',
                fontFamily: 'var(--font-family)'
              }}>
                Template Placeholders
              </h3>

              {template.placeholders && template.placeholders.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gap: '12px'
                }}>
                  {template.placeholders.map((placeholder, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0',
                        padding: '16px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <div>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: 'var(--text-primary)',
                            margin: '0 0 4px 0',
                            fontFamily: 'var(--font-family)'
                          }}>
                            {placeholder.name}
                          </h4>
                          {placeholder.description && (
                            <p style={{
                              fontSize: '12px',
                              color: 'var(--text-secondary)',
                              margin: '0 0 8px 0',
                              fontFamily: 'var(--font-family)'
                            }}>
                              {placeholder.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '0',
                            fontSize: '11px',
                            fontWeight: '500',
                            backgroundColor: placeholder.required ? '#fef3c7' : 'var(--accent-color)',
                            color: placeholder.required ? '#92400e' : 'var(--text-primary)',
                            fontFamily: 'var(--font-family)'
                          }}>
                            {placeholder.dataType}
                          </span>
                          {placeholder.required && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '0',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              fontFamily: 'var(--font-family)'
                            }}>
                              Required
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--secondary-color)',
                        borderRadius: '0',
                        border: '1px solid var(--border-color)'
                      }}>
                        <span style={{
                          fontSize: '13px',
                          color: 'var(--text-primary)',
                          flex: 1,
                          fontFamily: 'var(--font-family)'
                        }}>
                          {formatPlaceholderValue(placeholder, sampleData[placeholder.name])}
                        </span>
                        <button
                          onClick={() => copyToClipboard(
                            formatPlaceholderValue(placeholder, sampleData[placeholder.name]),
                            placeholder.name
                          )}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: 'var(--text-secondary)',
                            borderRadius: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {copiedField === placeholder.name ? (
                            <Check size={14} color="#4678be" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: 'var(--text-secondary)'
                }}>
                  <FileText size={32} style={{ marginBottom: '12px' }} />
                  <p style={{ margin: 0, fontFamily: 'var(--font-family)' }}>No placeholders defined for this template</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: '0 0 20px 0',
                fontFamily: 'var(--font-family)'
              }}>
                Template Settings
              </h3>

              <div style={{
                display: 'grid',
                gap: '20px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: '0 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    <FileType size={16} />
                    Template Information
                  </h4>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'var(--text-secondary)',
                        marginBottom: '4px',
                        fontFamily: 'var(--font-family)'
                      }}>
                        Template Type
                      </label>
                      <span style={{
                        fontSize: '14px',
                        color: 'var(--text-primary)',
                        fontWeight: '500',
                        fontFamily: 'var(--font-family)'
                      }}>
                        {template.templateType}
                      </span>
                    </div>
                    
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'var(--text-secondary)',
                        marginBottom: '4px',
                        fontFamily: 'var(--font-family)'
                      }}>
                        Version
                      </label>
                      <span style={{
                        fontSize: '14px',
                        color: 'var(--text-primary)',
                        fontWeight: '500',
                        fontFamily: 'var(--font-family)'
                      }}>
                        v{template.version}
                      </span>
                    </div>
                    
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'var(--text-secondary)',
                        marginBottom: '4px',
                        fontFamily: 'var(--font-family)'
                      }}>
                        Status
                      </label>
                      <span className={`badge ${
                        template.status === 'Active' ? 'badge-success' : 
                        template.status === 'Draft' ? 'badge-warning' : 
                        template.status === 'Deprecated' ? 'badge-danger' : 'badge-secondary'
                      }`} style={{ borderRadius: '0' }}>
                        {template.status}
                      </span>
                    </div>
                    
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'var(--text-secondary)',
                        marginBottom: '4px',
                        fontFamily: 'var(--font-family)'
                      }}>
                        Usage Count
                      </label>
                      <span style={{
                        fontSize: '14px',
                        color: 'var(--text-primary)',
                        fontWeight: '500',
                        fontFamily: 'var(--font-family)'
                      }}>
                        {template.usageCount || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: '0 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    <Tag size={16} />
                    Applicable Contract Types
                  </h4>
                  
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {template.applicableTypes && template.applicableTypes.length > 0 ? (
                      template.applicableTypes.map((type, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '0',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: '#4678be20',
                            color: '#4678be',
                            fontFamily: 'var(--font-family)'
                          }}
                        >
                          {type}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontFamily: 'var(--font-family)' }}>
                        No contract types specified
                      </span>
                    )}
                  </div>
                </div>

                {template.settings && (
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    padding: '20px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0 0 16px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: 'var(--font-family)'
                    }}>
                      <Settings size={16} />
                      Template Configuration
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary)',
                          marginBottom: '4px',
                          fontFamily: 'var(--font-family)'
                        }}>
                          Default Output Format
                        </label>
                        <span style={{
                          fontSize: '14px',
                          color: 'var(--text-primary)',
                          fontWeight: '500',
                          fontFamily: 'var(--font-family)'
                        }}>
                          {template.settings.defaultOutputFormat || 'Word'}
                        </span>
                      </div>
                      
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary)',
                          marginBottom: '4px',
                          fontFamily: 'var(--font-family)'
                        }}>
                          Include Clauses
                        </label>
                        <span style={{
                          fontSize: '14px',
                          color: 'var(--text-primary)',
                          fontWeight: '500',
                          fontFamily: 'var(--font-family)'
                        }}>
                          {template.settings.includeClauses ? 'Yes' : 'No'}
                        </span>
                      </div>
                      
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary)',
                          marginBottom: '4px',
                          fontFamily: 'var(--font-family)'
                        }}>
                          Include Documents
                        </label>
                        <span style={{
                          fontSize: '14px',
                          color: 'var(--text-primary)',
                          fontWeight: '500',
                          fontFamily: 'var(--font-family)'
                        }}>
                          {template.settings.includeDocuments ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
